import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Loader2, Search, RefreshCw, Users, CheckCircle2, Clock, AlertCircle, LogOut,
  TrendingUp, TrendingDown, ChevronRight, ChevronLeft, ChevronDown, Star, Target,
  BookOpen, UserCheck, BarChart3, X, FileText, Award, Zap, Lock, Info, ArrowRight,
  Cloud, Settings, Shield, Database, Brain, Download, Bell, Activity, Heart,
  Lightbulb, Users2, Layers, AlertTriangle
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REGION_NAMES = {
  usa: 'USA (SLIC)',
  canada: 'Canada (ivari)',
  jamaica: 'Jamaica',
  barbados: 'Barbados',
  trinidad_tobago: 'Trinidad & Tobago',
  panama: 'Panama'
};

const REGION_FLAGS = {
  usa: '🇺🇸',
  canada: '🇨🇦',
  jamaica: '🇯🇲',
  barbados: '🇧🇧',
  trinidad_tobago: '🇹🇹',
  panama: '🇵🇦'
};

// VP Growth Tier Badge (Green/Blue/Amber only - NO RED)
const GrowthTierBadge = ({ tier, size = "md" }) => {
  if (!tier) return <span className="text-xs text-muted-foreground">—</span>;
  const colors = {
    "Builder": { bg: '#E8F5E9', text: '#1B5E20' },
    "Accelerating": { bg: '#E3F2FD', text: '#1565C0' },
    "Growing": { bg: '#FFF8E1', text: '#E65100' }
  };
  const c = colors[tier] || { bg: '#F3F4F6', text: '#6B7280' };
  const sizeClasses = size === "lg" ? "px-3 py-1.5 text-sm" : "px-2 py-0.5 text-xs";
  return (
    <span className={`rounded font-bold ${sizeClasses}`} style={{ backgroundColor: c.bg, color: c.text }}>
      {tier.toUpperCase()}
    </span>
  );
};

// Star Rating Component
const StarRating = ({ rating, max = 5 }) => (
  <div className="flex items-center gap-0.5">
    {[...Array(max)].map((_, i) => (
      <Star key={i} className={`w-3 h-3 ${i < rating ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`} />
    ))}
  </div>
);

// Empty data for clean start - will be populated from API
const MOCK_COACHABILITY = { detailed: 0, adequate: 0, limited: 0 };
const MOCK_CROSS_TEAM_DEPS = [];
const MOCK_PERCEPTION_GAP = [];
const MOCK_KEY_PERSON_RISK = [];
const MOCK_COLLABORATION = [];
const MOCK_ICARE = [];
const MOCK_MENTORSHIP_PAIRS = [];

const INITIATIVE_TYPES = [
  { id: 'cloud', name: 'Cloud Migration', icon: Cloud, primary: 'Infrastructure & DevOps', secondary: 'Security', supporting: 'Architecture' },
  { id: 'microservices', name: 'Microservices Rebuild', icon: Settings, primary: 'Architecture & Design', secondary: 'Programming & APIs', supporting: 'Testing & QA' },
  { id: 'data', name: 'Data Pipeline', icon: Database, primary: 'Data & MLOps', secondary: 'Programming & APIs', supporting: 'Infrastructure' },
  { id: 'security', name: 'Security Hardening', icon: Shield, primary: 'Security & Reliability', secondary: 'Infrastructure', supporting: 'Testing & QA' },
  { id: 'boat', name: 'BOAT Implementation', icon: Zap, primary: 'Automation & BOAT', secondary: 'Architecture', supporting: 'Programming & APIs' },
  { id: 'ai', name: 'AI/ML Integration', icon: Brain, primary: 'AI-Augmented Dev', secondary: 'Data & MLOps', supporting: 'Programming & APIs' },
];

export default function RegionalVPDashboard() {
  const { region } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("my-team");
  const [vpInfo, setVpInfo] = useState({});
  const [stats, setStats] = useState({ total: 0, completed: 0, inProgress: 0, notStarted: 0, completionRate: 0 });
  const [teamMembers, setTeamMembers] = useState([]);
  const [capabilities, setCapabilities] = useState({});
  const [validations, setValidations] = useState({ pending: [], completed: [], totalPending: 0 });
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedInitiative, setSelectedInitiative] = useState(null);
  const [teamFilter, setTeamFilter] = useState('all');
  const [validationForm, setValidationForm] = useState({ ratings: {}, narrative: '', endorsement: '' });

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("authToken") || localStorage.getItem("vpToken") || localStorage.getItem("token");
    return { headers: { Authorization: `Bearer ${token}` } };
  }, []);

  const fetchDashboardData = useCallback(async () => {
    // Check if we have a token before making requests
    const token = localStorage.getItem("authToken") || localStorage.getItem("vpToken") || localStorage.getItem("token");
    if (!token) {
      console.log("No auth token found, redirecting to login");
      navigate("/login");
      return;
    }
    
    try {
      setLoading(true);
      const [dashboardRes, teamRes, capRes, valRes] = await Promise.all([
        axios.get(`${API}/regional/${region}/vp/dashboard`, getAuthHeaders()),
        axios.get(`${API}/regional/${region}/vp/team?limit=200`, getAuthHeaders()),
        axios.get(`${API}/regional/${region}/vp/capabilities`, getAuthHeaders()),
        axios.get(`${API}/regional/${region}/vp/validations`, getAuthHeaders())
      ]);
      
      setVpInfo(dashboardRes.data.vpInfo || {});
      // Map API field 'started' to frontend field 'inProgress'
      const apiStats = dashboardRes.data.stats || {};
      setStats({
        ...apiStats,
        inProgress: apiStats.started || 0,
        notStarted: apiStats.pending || 0
      });
      setTeamMembers(teamRes.data.teamMembers || []);
      setCapabilities(capRes.data || {});
      setValidations(valRes.data || { pending: [], completed: [], totalPending: 0 });
    } catch (err) {
      console.error("Error fetching VP dashboard data:", err);
      if (err.response?.status === 401) {
        // Clear any stale tokens
        localStorage.removeItem("authToken");
        localStorage.removeItem("vpToken");
        localStorage.removeItem("token");
        toast.error("Session expired. Please login again.");
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  }, [region, getAuthHeaders, navigate]);

  useEffect(() => { fetchDashboardData(); }, [fetchDashboardData]);

  // If no user data loaded, show login prompt
  const [authError, setAuthError] = useState(false);
  
  useEffect(() => {
    const token = localStorage.getItem("authToken") || localStorage.getItem("vpToken") || localStorage.getItem("token");
    if (!token) {
      setAuthError(true);
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("vpToken");
    localStorage.removeItem("token");
    localStorage.removeItem("userRole");
    localStorage.removeItem("userInfo");
    navigate("/login");
  };

  const filteredTeam = teamMembers.filter(m => {
    if (teamFilter === 'all') return true;
    if (teamFilter === 'completed') return m.status === 'completed';
    if (teamFilter === 'in-progress') return m.status === 'started';
    if (teamFilter === 'not-started') return m.status === 'pending';
    return true;
  });

  // Calculate team metrics - only count members who have completed assessments
  const completedMembers = teamMembers.filter(m => m.status === 'completed' || m.briScore > 0);
  const teamAvgBRI = completedMembers.length > 0 ? Math.round(completedMembers.reduce((sum, m) => sum + (m.briScore || 0), 0) / completedMembers.length) : 0;
  const builders = completedMembers.filter(m => (m.briScore || 0) >= 85).length;
  const accelerating = completedMembers.filter(m => (m.briScore || 0) >= 70 && (m.briScore || 0) < 85).length;
  const growing = completedMembers.filter(m => (m.briScore || 0) < 70).length;

  // Show auth error screen if no token
  if (authError) {
    return (
      <div className="min-h-screen bg-[#F5F3F0] flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-xl shadow-lg max-w-md">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-amber-600" />
          </div>
          <h2 className="text-xl font-semibold text-[#1a3a3a] mb-2">Session Expired</h2>
          <p className="text-muted-foreground mb-6">Please log in to access your dashboard.</p>
          <Button 
            onClick={() => navigate("/login")}
            className="bg-[#00AEEF] hover:bg-[#0095cc] text-white px-8"
          >
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F3F0] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-[#1a3a3a]" />
          <p className="text-muted-foreground">Loading your team dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F3F0]">
      {/* Header */}
      <header className="bg-[#1a3a3a] text-white sticky top-0 z-50">
        <div className="max-w-[1500px] mx-auto px-6">
          <div className="h-14 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-7 h-7 bg-[#C4A265] rounded-full flex items-center justify-center text-sm font-bold text-[#1a3a3a]">S</div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-base font-semibold">Workforce Intelligence</span>
                  <Badge className="bg-transparent border border-[#F59E0B] text-[#F59E0B] text-[10px] px-1.5">TEAM VIEW</Badge>
                </div>
                <p className="text-xs text-white/70">{REGION_FLAGS[region]} {REGION_NAMES[region]}</p>
              </div>
            </div>
            
            <div className="hidden md:flex items-center gap-2 bg-[#2a4a4a] rounded-lg px-3 py-1.5">
              <Search className="w-4 h-4 text-white/50" />
              <input type="text" placeholder="Search team member..." className="bg-transparent border-none outline-none text-sm text-white placeholder:text-white/50 w-48" />
              <span className="text-[10px] text-white/40">⌘K</span>
            </div>

            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" className="text-white/70 hover:text-white relative">
                <Bell className="w-5 h-5" />
                {validations.totalPending > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#F59E0B] rounded-full text-[10px] flex items-center justify-center">{validations.totalPending}</span>
                )}
              </Button>
              <Button variant="outline" size="sm" className="text-white border-white/30 hover:bg-white/10 text-xs gap-1">
                <Download className="w-3 h-3" />
                Export Team Report
              </Button>
              <div className="flex items-center gap-3">
                <div className="text-right hidden sm:block">
                  <p className="text-sm font-medium">{vpInfo.name}</p>
                  <p className="text-[10px] text-white/70">{vpInfo.title}</p>
                </div>
                <div className="w-8 h-8 bg-[#F59E0B] rounded-full flex items-center justify-center text-xs font-bold text-[#1a3a3a]">
                  {vpInfo.name?.split(' ').map(n => n[0]).join('').slice(0, 2) || 'VP'}
                </div>
                <Button variant="ghost" size="icon" className="text-white/70 hover:text-white" onClick={handleLogout}>
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* 5-Tab Navigation */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-[1500px] mx-auto px-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="h-12 bg-transparent border-0 gap-2 w-full justify-start">
              {[
                { id: 'my-team', label: 'My Team', icon: Users },
                { id: 'intelligence', label: 'Team Intelligence', icon: BarChart3 },
                { id: 'development', label: 'Development & Growth', icon: BookOpen },
                { id: 'readiness', label: 'Project Readiness', icon: Target },
                { id: 'validation', label: 'Validation Queue', icon: UserCheck, badge: validations.totalPending },
              ].map((tab) => (
                <TabsTrigger 
                  key={tab.id}
                  value={tab.id}
                  className="data-[state=active]:bg-[#1a3a3a] data-[state=active]:text-white data-[state=active]:shadow-none data-[state=active]:border-b-[3px] data-[state=active]:border-[#F59E0B] rounded-t-md px-4 text-[13px] font-medium"
                >
                  <tab.icon className="w-4 h-4 mr-2" />
                  {tab.label}
                  {tab.badge > 0 && (
                    <Badge className="ml-2 bg-[#F59E0B] text-[#1a3a3a] text-[10px] h-5 min-w-5">{tab.badge}</Badge>
                  )}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* TAB 1: MY TEAM */}
      {activeTab === "my-team" && (
        <main className="max-w-[1500px] mx-auto px-6 py-6">
          {/* Welcome Header */}
          <Card className="bg-[#F9FAFB] border-0 shadow-sm mb-6">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-[#1a3a3a]">Welcome, {vpInfo.name?.split(' ')[0]}</h2>
                  <p className="text-sm text-muted-foreground">{vpInfo.title} — {vpInfo.department}</p>
                  <p className="text-xs text-muted-foreground mt-1">Team Size: {stats.total} professionals</p>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Clock className="w-3 h-3" />
                  Last updated: Just now
                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={fetchDashboardData}>
                    <RefreshCw className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <Card className="bg-white shadow-sm border-l-4 border-l-[#1a3a3a]">
              <CardContent className="p-4">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide font-medium">My Team</p>
                <p className="text-3xl font-bold text-[#1a3a3a]">{stats.total}</p>
                <Progress value={stats.completionRate} className="mt-2 h-1.5" />
                <p className="text-[10px] text-muted-foreground mt-1">{stats.completionRate}% assessed</p>
              </CardContent>
            </Card>
            <Card className="bg-white shadow-sm border-l-4 border-l-emerald-500">
              <CardContent className="p-4">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide font-medium">Completed</p>
                <p className="text-3xl font-bold text-emerald-600">{stats.completed || 0}</p>
                <p className="text-xs text-muted-foreground mt-1">Assessments done</p>
              </CardContent>
            </Card>
            <Card className="bg-white shadow-sm border-l-4 border-l-blue-500">
              <CardContent className="p-4">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide font-medium">In Progress</p>
                <p className="text-3xl font-bold text-blue-600">{stats.inProgress || 0}</p>
                <p className="text-xs text-muted-foreground mt-1">Currently active</p>
              </CardContent>
            </Card>
            <Card className="bg-white shadow-sm border-l-4 border-l-slate-400">
              <CardContent className="p-4">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide font-medium">Pending</p>
                <p className="text-3xl font-bold text-slate-600">{stats.pending || (stats.total - (stats.completed || 0) - (stats.inProgress || 0))}</p>
                <p className="text-xs text-muted-foreground mt-1">Not started yet</p>
              </CardContent>
            </Card>
            <Card className={`bg-white shadow-sm border-l-4 ${stats.completionRate >= 75 ? 'border-l-emerald-500' : stats.completionRate >= 50 ? 'border-l-amber-500' : 'border-l-amber-500'}`}>
              <CardContent className="p-4">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide font-medium">Completion Rate</p>
                <p className={`text-3xl font-bold ${stats.completionRate >= 75 ? 'text-emerald-600' : stats.completionRate >= 50 ? 'text-amber-600' : 'text-amber-600'}`}>{stats.completionRate}%</p>
                <p className="text-xs text-muted-foreground mt-1">Of team assessed</p>
              </CardContent>
            </Card>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Team Table (60%) */}
            <div className="lg:col-span-3">
              <Card className="bg-white shadow-sm">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-semibold text-[#1a3a3a]">My Team ({filteredTeam.length})</CardTitle>
                    <div className="flex gap-1">
                      {['all', 'completed', 'in-progress', 'not-started'].map((f) => (
                        <Button 
                          key={f} 
                          variant={teamFilter === f ? "default" : "outline"} 
                          size="sm" 
                          className={`text-xs h-7 ${teamFilter === f ? 'bg-[#1a3a3a]' : ''}`}
                          onClick={() => setTeamFilter(f)}
                        >
                          {f === 'all' ? 'All' : f === 'completed' ? 'Completed' : f === 'in-progress' ? 'In Progress' : 'Not Started'}
                        </Button>
                      ))}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="overflow-y-auto max-h-[500px]">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-white">
                        <tr className="border-b">
                          <th className="text-left py-2 px-2 text-xs font-medium text-muted-foreground">Name</th>
                          <th className="text-left py-2 px-2 text-xs font-medium text-muted-foreground">Title</th>
                          <th className="text-left py-2 px-2 text-xs font-medium text-muted-foreground">Reports To</th>
                          <th className="text-center py-2 px-2 text-xs font-medium text-muted-foreground">Status</th>
                          <th className="text-center py-2 px-2 text-xs font-medium text-muted-foreground">BRI</th>
                          <th className="text-center py-2 px-2 text-xs font-medium text-muted-foreground">Tier</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredTeam.map((member) => (
                          <tr 
                            key={member.id} 
                            className="border-b hover:bg-slate-50 cursor-pointer"
                            onClick={() => { setSelectedEmployee(member); setActiveTab('development'); }}
                          >
                            <td className="py-2 px-2">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-[#1a3a3a] flex items-center justify-center text-white text-xs font-medium">
                                  {member.fullName?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                                </div>
                                <div>
                                  <p className="text-sm font-medium">{member.fullName}</p>
                                  <p className="text-[10px] text-muted-foreground">{member.email}</p>
                                </div>
                              </div>
                            </td>
                            <td className="py-2 px-2 text-xs">{member.jobTitle}</td>
                            <td className="py-2 px-2 text-xs text-muted-foreground">{member.reportsTo || '—'}</td>
                            <td className="py-2 px-2 text-center">
                              <Badge className={`text-[10px] ${
                                member.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                                member.status === 'started' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'
                              }`}>
                                {member.status === 'completed' ? 'Complete' : member.status === 'started' ? 'In Progress' : 'Not Started'}
                              </Badge>
                            </td>
                            <td className="py-2 px-2 text-center">
                              <span className={`text-lg font-bold ${
                                (member.briScore || 0) >= 85 ? 'text-[#1B5E20]' : 
                                (member.briScore || 0) >= 70 ? 'text-[#1565C0]' : 'text-[#E65100]'
                              }`}>
                                {member.briScore || '—'}
                              </span>
                            </td>
                            <td className="py-2 px-2 text-center">
                              <GrowthTierBadge tier={member.growthTier} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Sidebar (40%) */}
            <div className="lg:col-span-2 space-y-4">
              {/* Coachability Signals */}
              <Card className="bg-white shadow-sm">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-sm font-semibold text-[#1a3a3a]">Coachability Signals</CardTitle>
                    <Info className="w-3 h-3 text-muted-foreground" />
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {[
                    { label: 'Detailed & Reflective', count: MOCK_COACHABILITY.detailed, color: '#16A34A' },
                    { label: 'Adequate', count: MOCK_COACHABILITY.adequate, color: '#6B7280' },
                    { label: 'Limited Self-Reflection', count: MOCK_COACHABILITY.limited, color: '#F59E0B' },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color }} />
                      <span className="text-xs flex-1">{item.label}</span>
                      <span className="text-xs font-medium">{item.count}</span>
                      <div className="w-20 h-1.5 bg-slate-100 rounded">
                        <div className="h-full rounded" style={{ width: `${(item.count / stats.total) * 100}%`, backgroundColor: item.color }} />
                      </div>
                    </div>
                  ))}
                  <div className="p-2 bg-blue-50 rounded border-l-2 border-blue-400 mt-2">
                    <p className="text-[10px] text-blue-700">
                      <Lightbulb className="w-3 h-3 inline mr-1" />
                      {MOCK_COACHABILITY.limited} team members may benefit from structured self-reflection exercises.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Assessment Phase Tracker */}
              <Card className="bg-white shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-[#1a3a3a]">Assessment Phase Tracker</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {[
                    { phase: 'Self Assessment', completed: (stats.inProgress || 0) + (stats.completed || 0), total: stats.total, status: 'active' },
                    { phase: 'Technical Assessment', completed: 0, total: stats.total, status: 'upcoming' },
                    { phase: 'Manager Validation', completed: 0, total: stats.total, status: 'upcoming', pending: validations.totalPending },
                    { phase: 'Hands-On Simulation', completed: 0, total: stats.total, status: 'upcoming' },
                  ].map((item) => (
                    <div key={item.phase} className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium">{item.phase}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] text-muted-foreground">{item.completed}/{item.total}</span>
                          <Badge className={`text-[9px] ${
                            item.status === 'complete' ? 'bg-emerald-100 text-emerald-700' :
                            item.status === 'active' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'
                          }`}>
                            {item.status === 'active' ? 'ACTIVE' : item.status === 'complete' ? 'DONE' : 'UPCOMING'}
                          </Badge>
                        </div>
                      </div>
                      <Progress value={(item.completed / item.total) * 100} className="h-1.5" />
                      {item.pending > 0 && (
                        <p className="text-[10px] text-amber-600 cursor-pointer hover:underline" onClick={() => setActiveTab('validation')}>
                          ⚠ {item.pending} validations awaiting your input →
                        </p>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Live Activity */}
              <Card className="bg-white shadow-sm">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                    <CardTitle className="text-sm font-semibold text-[#1a3a3a]">Live Team Activity</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 text-center py-4">
                    <p className="text-xs text-muted-foreground">
                      No recent activity
                    </p>
                    <p className="text-[10px] text-muted-foreground">
                      Team activity will appear here once assessments begin
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Cross-Team Dependencies */}
          <Card className="bg-white shadow-sm mt-6">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <CardTitle className="text-base font-semibold text-[#1a3a3a]">Your Team Members Supporting Other Functions</CardTitle>
                <Info className="w-4 h-4 text-muted-foreground" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-2 text-xs font-medium text-muted-foreground">Team Member</th>
                      <th className="text-left py-2 px-2 text-xs font-medium text-muted-foreground">Shared Capability</th>
                      <th className="text-left py-2 px-2 text-xs font-medium text-muted-foreground">Consuming Teams</th>
                      <th className="text-center py-2 px-2 text-xs font-medium text-muted-foreground">Load</th>
                    </tr>
                  </thead>
                  <tbody>
                    {MOCK_CROSS_TEAM_DEPS.map((dep, i) => (
                      <tr key={i} className="border-b">
                        <td className="py-2 px-2">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-medium">
                              {dep.name.split(' ').map(n => n[0]).join('')}
                            </div>
                            <span className="text-xs font-medium">{dep.name}</span>
                          </div>
                        </td>
                        <td className="py-2 px-2 text-xs">{dep.capability}</td>
                        <td className="py-2 px-2 text-xs text-muted-foreground">{dep.teams.join(', ')}</td>
                        <td className="py-2 px-2 text-center">
                          <div className="flex items-center justify-center gap-0.5">
                            {[1, 2, 3, 4].map((dot) => (
                              <div 
                                key={dot} 
                                className={`w-2 h-2 rounded-full ${
                                  dep.load === 'heavy' && dot <= 3 ? 'bg-amber-500' :
                                  dep.load === 'moderate' && dot <= 2 ? 'bg-blue-500' :
                                  dep.load === 'light' && dot <= 1 ? 'bg-emerald-500' : 'bg-slate-200'
                                }`} 
                              />
                            ))}
                            <span className={`ml-1 text-[10px] ${
                              dep.load === 'heavy' ? 'text-amber-600' : dep.load === 'moderate' ? 'text-blue-600' : 'text-emerald-600'
                            }`}>
                              {dep.load.charAt(0).toUpperCase() + dep.load.slice(1)}
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="p-2 bg-slate-50 rounded border-l-2 border-slate-300 mt-3">
                <p className="text-[10px] text-slate-600">
                  <Lightbulb className="w-3 h-3 inline mr-1" />
                  Cross-team dependencies will appear once assessments are completed.
                </p>
              </div>
            </CardContent>
          </Card>
        </main>
      )}

      {/* TAB 2: TEAM INTELLIGENCE */}
      {activeTab === "intelligence" && (
        <main className="max-w-[1500px] mx-auto px-6 py-6">
          {/* Team Readiness Banner */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <Card className={`bg-white shadow-sm border-l-4 ${teamAvgBRI >= 70 ? 'border-l-emerald-500' : teamAvgBRI >= 55 ? 'border-l-blue-500' : 'border-l-amber-500'}`}>
              <CardContent className="p-4 text-center">
                <p className="text-[10px] text-muted-foreground uppercase">Team Average BRI</p>
                <p className={`text-4xl font-bold ${teamAvgBRI >= 70 ? 'text-emerald-600' : teamAvgBRI >= 55 ? 'text-blue-600' : 'text-amber-600'}`}>{teamAvgBRI}</p>
                <p className="text-xs text-muted-foreground">Awaiting data</p>
              </CardContent>
            </Card>
            <Card className="bg-white shadow-sm">
              <CardContent className="p-4">
                <div className="flex justify-center gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-[#1B5E20]">{builders}</p>
                    <Badge className="bg-[#E8F5E9] text-[#1B5E20] text-[10px]">BUILDER</Badge>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-[#1565C0]">{accelerating}</p>
                    <Badge className="bg-[#E3F2FD] text-[#1565C0] text-[10px]">ACCELERATING</Badge>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-[#E65100]">{growing}</p>
                    <Badge className="bg-[#FFF8E1] text-[#E65100] text-[10px]">GROWING</Badge>
                  </div>
                </div>
                <div className="flex mt-3 rounded overflow-hidden h-3">
                  <div style={{ width: `${(builders / (stats.total || 1)) * 100}%`, backgroundColor: '#1B5E20' }} />
                  <div style={{ width: `${(accelerating / (stats.total || 1)) * 100}%`, backgroundColor: '#1565C0' }} />
                  <div style={{ width: `${(growing / (stats.total || 1)) * 100}%`, backgroundColor: '#F59E0B' }} />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white shadow-sm">
              <CardContent className="p-4 text-center">
                <p className="text-[10px] text-muted-foreground uppercase">Team Trend</p>
                <div className="flex items-center justify-center gap-2 mt-2">
                  <BarChart3 className="w-6 h-6 text-slate-400" />
                  <span className="text-lg font-medium text-slate-400">—</span>
                </div>
                <p className="text-xs text-muted-foreground">Awaiting data</p>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-2 gap-6 mb-6">
            {/* Perception Gap Analysis */}
            <Card className="bg-white shadow-sm">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-base font-semibold text-[#1a3a3a]">Self-Awareness Landscape</CardTitle>
                  <Info className="w-4 h-4 text-muted-foreground" />
                </div>
                <p className="text-xs text-muted-foreground">Self-Assessment vs Assessed Score</p>
              </CardHeader>
              <CardContent>
                <div className="relative h-64 border rounded bg-slate-50">
                  {/* Quadrant backgrounds */}
                  <div className="absolute top-0 left-0 w-1/2 h-1/2 bg-[#E8F5E9]/50" />
                  <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-[#E3F2FD]/50" />
                  <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-[#FFF8E1]/50" />
                  <div className="absolute bottom-0 right-0 w-1/2 h-1/2 bg-[#FFF3E0]/50" />
                  {/* Diagonal line */}
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(to top right, transparent calc(50% - 1px), #94a3b8 calc(50% - 1px), #94a3b8 calc(50% + 1px), transparent calc(50% + 1px))' }} />
                  {/* Quadrant labels */}
                  <span className="absolute top-2 left-2 text-[9px] text-emerald-700 font-medium">Hidden Potential</span>
                  <span className="absolute top-2 right-2 text-[9px] text-blue-700 font-medium">Confident & Capable</span>
                  <span className="absolute bottom-2 left-2 text-[9px] text-amber-700 font-medium">Needs Foundation</span>
                  <span className="absolute bottom-2 right-2 text-[9px] text-orange-700 font-medium bg-orange-100 px-1 rounded">Coaching Opportunity (0)</span>
                  {/* Dots */}
                  {MOCK_PERCEPTION_GAP.map((p, i) => {
                    const x = (p.selfScore / 100) * 100;
                    const y = 100 - (p.assessedScore / 100) * 100;
                    const color = p.tier === 'Builder' ? '#1B5E20' : p.tier === 'Accelerating' ? '#1565C0' : '#F59E0B';
                    return (
                      <div 
                        key={i}
                        className="absolute w-3 h-3 rounded-full border-2 border-white shadow-sm cursor-pointer hover:scale-150 transition-transform"
                        style={{ left: `${x}%`, top: `${y}%`, backgroundColor: color, transform: 'translate(-50%, -50%)' }}
                        title={`${p.name}: Self ${p.selfScore} / Assessed ${p.assessedScore}`}
                      />
                    );
                  })}
                  {/* Axis labels */}
                  <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-4 text-[9px] text-muted-foreground">Self-Assessment Score →</span>
                  <span className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-6 rotate-[-90deg] text-[9px] text-muted-foreground">Assessed Score →</span>
                </div>
                <div className="p-2 bg-slate-50 rounded border-l-2 border-slate-300 mt-3">
                  <p className="text-[10px] text-slate-600">
                    Data will populate once assessments are completed.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Score Distribution */}
            <Card className="bg-white shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold text-[#1a3a3a]">Team Score Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-end gap-2 h-48">
                  {[
                    { range: '0-54', count: growing, color: '#F59E0B' },
                    { range: '55-69', count: 0, color: '#F59E0B' },
                    { range: '70-79', count: accelerating, color: '#1565C0' },
                    { range: '80-84', count: 0, color: '#1565C0' },
                    { range: '85+', count: builders, color: '#1B5E20' },
                  ].map((bar) => {
                    const maxCount = Math.max(growing, accelerating, builders) || 1;
                    const height = (bar.count / maxCount) * 100;
                    return (
                      <div key={bar.range} className="flex-1 flex flex-col items-center">
                        <span className="text-xs font-medium mb-1">{bar.count}</span>
                        <div className="w-full rounded-t" style={{ height: `${Math.max(height, 10)}%`, backgroundColor: bar.color }} />
                        <span className="text-[9px] text-muted-foreground mt-1">{bar.range}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center justify-between mt-2 pt-2 border-t text-xs text-muted-foreground">
                  <span>Mean: {teamAvgBRI}</span>
                  <span>Median: {teamAvgBRI - 2}</span>
                  <div className="flex items-center gap-1">
                    <div className="w-px h-4 border-l-2 border-dashed border-blue-400" />
                    <span>70 (Accelerating)</span>
                    <div className="w-px h-4 border-l-2 border-dashed border-emerald-400 ml-2" />
                    <span>85 (Builder)</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Skills Heat Map */}
          <Card className="bg-white shadow-sm mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold text-[#1a3a3a]">Skills Capability Heat Map</CardTitle>
            </CardHeader>
            <CardContent>
              {stats.total > 0 ? (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-2 font-medium text-muted-foreground">Team</th>
                          <th className="text-center py-2 px-2">Programming</th>
                          <th className="text-center py-2 px-2">DevOps</th>
                          <th className="text-center py-2 px-2">Security</th>
                          <th className="text-center py-2 px-2">Testing</th>
                          <th className="text-center py-2 px-2">Data/ML</th>
                          <th className="text-center py-2 px-2">Architecture</th>
                          <th className="text-center py-2 px-2">BOAT</th>
                          <th className="text-center py-2 px-2">AI-Aug</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { team: 'My Team', scores: [capabilities.programming || 0, capabilities.devops || 0, capabilities.security || 0, capabilities.testing || 0, capabilities.data || 0, capabilities.architecture || 0, capabilities.automation || 0, capabilities.ai || 0] }
                        ].map((row) => (
                          <tr key={row.team} className="border-b">
                            <td className="py-2 px-2 font-medium">{row.team}</td>
                            {row.scores.map((score, i) => {
                              const bg = score >= 70 ? '#1B5E20' : score >= 55 ? '#E8F5E9' : score >= 40 ? '#FFF8E1' : '#FFF3E0';
                              const text = score >= 70 ? 'white' : score >= 55 ? '#1B5E20' : '#E65100';
                              return (
                                <td key={i} className="py-2 px-2 text-center">
                                  <span className="inline-block w-10 py-1 rounded font-bold" style={{ backgroundColor: bg, color: text }}>{score}</span>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex gap-4 mt-3 text-[10px]">
                    <span className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-[#1B5E20]" /> Strong (70+)</span>
                    <span className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-[#E8F5E9] border" /> Adequate (55-69)</span>
                    <span className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-[#FFF8E1]" /> Developing (40-54)</span>
                    <span className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-[#FFF3E0]" /> Growth Needed (&lt;40)</span>
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <p className="text-sm">Skills data will populate once assessments are completed</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Three Intelligence Panels */}
          <div className="grid grid-cols-3 gap-6">
            {/* Key Person Risk Map */}
            <Card className="bg-white shadow-sm">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-sm font-semibold text-[#1a3a3a]">Operational Continuity</CardTitle>
                  <Info className="w-3 h-3 text-muted-foreground" />
                </div>
              </CardHeader>
              <CardContent>
                {stats.total > 0 ? (
                  <div className="space-y-2">
                    {MOCK_KEY_PERSON_RISK.map((item, i) => (
                      <div key={i} className="p-2 bg-slate-50 rounded">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] font-medium truncate flex-1 mr-2">{item.capability}</span>
                          <span className={`text-[9px] font-bold ${
                            item.coverage === 'critical' ? 'text-red-600' : item.coverage === 'limited' ? 'text-amber-600' : 'text-emerald-600'
                          }`}>
                            {item.coverage === 'critical' ? '🔴 Critical' : item.coverage === 'limited' ? '🟡 Limited' : '🟢 Healthy'}
                          </span>
                        </div>
                        <div className="flex gap-2 text-[9px] text-muted-foreground">
                          <span className="text-emerald-600">{item.proficient} proficient</span>
                          <span className="text-blue-600">{item.developing} developing</span>
                          <span className="text-amber-600">{item.learning} learning</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted-foreground text-xs">
                    Awaiting assessment data
                  </div>
                )}
                <div className="p-2 bg-slate-50 rounded border-l-2 border-slate-300 mt-3">
                  <p className="text-[10px] text-slate-600">
                    Awaiting assessment data
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Collaboration Scorecard */}
            <Card className="bg-white shadow-sm">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-sm font-semibold text-[#1a3a3a]">Beyond Technical Skill</CardTitle>
                  <Info className="w-3 h-3 text-muted-foreground" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  {[
                    { label: 'Force Multipliers', count: 0, color: '#1B5E20', bg: '#E8F5E9' },
                    { label: 'Team Anchors', count: 0, color: '#1565C0', bg: '#E3F2FD' },
                    { label: 'Solo Contributors', count: 0, color: '#E65100', bg: '#FFF8E1' },
                    { label: 'Growing All-Around', count: 0, color: '#F59E0B', bg: '#FFF8E1' },
                  ].map((quad) => (
                    <div key={quad.label} className="p-2 rounded text-center" style={{ backgroundColor: quad.bg }}>
                      <p className="text-lg font-bold" style={{ color: quad.color }}>{quad.count}</p>
                      <p className="text-[9px]" style={{ color: quad.color }}>{quad.label}</p>
                    </div>
                  ))}
                </div>
                <div className="p-2 bg-blue-50 rounded border-l-2 border-blue-400">
                  <p className="text-[10px] text-blue-700">
                    💡 Consider pairing solo contributors with team anchors for upcoming projects.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* ICARE Values */}
            <Card className="bg-white shadow-sm">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-sm font-semibold text-[#1a3a3a]">Team Culture Champions</CardTitle>
                  <Heart className="w-3 h-3 text-red-500" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {MOCK_ICARE.map((item) => (
                    <div key={item.value} className="flex items-center gap-2">
                      <span className="text-[10px] w-24">{item.value}</span>
                      <div className="flex-1 h-2 bg-slate-100 rounded overflow-hidden">
                        <div className={`h-full ${item.score >= 70 ? 'bg-emerald-500' : 'bg-amber-500'}`} style={{ width: `${item.score}%` }} />
                      </div>
                      <span className="text-[10px] font-medium w-8">{item.score}</span>
                      <Badge className="bg-[#C4A265] text-white text-[8px] px-1">{item.champions}★</Badge>
                    </div>
                  ))}
                </div>
                <div className="p-2 bg-purple-50 rounded border-l-2 border-purple-400 mt-3">
                  <p className="text-[10px] text-purple-700">
                    💡 Ensure at least one culture champion is assigned to each active workstream.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Team Composition Health */}
          <Card className="bg-white shadow-sm mt-6">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <CardTitle className="text-base font-semibold text-[#1a3a3a]">Team Structure Analysis</CardTitle>
                <Info className="w-4 h-4 text-muted-foreground" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-6">
                {/* Tenure */}
                <div>
                  <p className="text-xs font-medium mb-2">Tenure Distribution</p>
                  <div className="h-4 flex rounded overflow-hidden">
                    <div className="bg-blue-300" style={{ width: '15%' }} title="<2 years" />
                    <div className="bg-blue-400" style={{ width: '25%' }} title="2-5 years" />
                    <div className="bg-blue-500" style={{ width: '35%' }} title="5-10 years" />
                    <div className="bg-blue-700" style={{ width: '25%' }} title="10+ years" />
                  </div>
                  <div className="flex justify-between text-[9px] text-muted-foreground mt-1">
                    <span>&lt;2y (3)</span>
                    <span>2-5y (5)</span>
                    <span>5-10y (7)</span>
                    <span>10+y (5)</span>
                  </div>
                </div>
                {/* Seniority */}
                <div>
                  <p className="text-xs font-medium mb-2">Seniority Balance</p>
                  <div className="h-4 flex rounded overflow-hidden">
                    <div className="bg-amber-400" style={{ width: `${(growing / stats.total) * 100}%` }} title="Growing" />
                    <div className="bg-blue-500" style={{ width: `${(accelerating / stats.total) * 100}%` }} title="Accelerating" />
                    <div className="bg-emerald-600" style={{ width: `${(builders / stats.total) * 100}%` }} title="Builder" />
                  </div>
                  <div className="flex justify-between text-[9px] text-muted-foreground mt-1">
                    <span>Growing ({growing})</span>
                    <span>Accelerating ({accelerating})</span>
                    <span>Builder ({builders})</span>
                  </div>
                </div>
                {/* Domain Coverage */}
                <div>
                  <p className="text-xs font-medium mb-2">Domain Coverage (Proficient 70+)</p>
                  <div className="grid grid-cols-4 gap-1">
                    {['Prog', 'DevOps', 'Security', 'Test', 'Data', 'Arch', 'BOAT', 'AI'].map((d, i) => {
                      const count = [4, 3, 2, 3, 1, 2, 0, 0][i];
                      return (
                        <div key={d} className={`p-1 text-center text-[9px] rounded ${count >= 3 ? 'bg-emerald-100 text-emerald-700' : count >= 1 ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                          {d}: {count}
                        </div>
                      );
                    })}
                  </div>
                  {[0, 0].some(c => c === 0) && (
                    <p className="text-[9px] text-amber-600 mt-1">⚠️ 0 proficient in BOAT, AI domains</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </main>
      )}

      {/* TAB 3: DEVELOPMENT & GROWTH */}
      {activeTab === "development" && (
        <main className="max-w-[1500px] mx-auto px-6 py-6">
          {/* Employee Selector */}
          <div className="flex items-center justify-between mb-6">
            <Button variant="ghost" size="sm" onClick={() => setActiveTab('intelligence')} className="text-xs gap-1">
              <ChevronLeft className="w-3 h-3" /> Back to Team Intelligence
            </Button>
            <Select value={selectedEmployee?.id || ''} onValueChange={(id) => setSelectedEmployee(teamMembers.find(m => m.id === id))}>
              <SelectTrigger className="w-64">
                <SelectValue placeholder="Select team member..." />
              </SelectTrigger>
              <SelectContent>
                {teamMembers.map((m) => (
                  <SelectItem key={m.id} value={m.id}>{m.fullName}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="text-xs gap-1">
                <ChevronLeft className="w-3 h-3" /> Previous
              </Button>
              <Button variant="outline" size="sm" className="text-xs gap-1">
                Next <ChevronRight className="w-3 h-3" />
              </Button>
              <Button variant="outline" size="sm" className="text-xs gap-1">
                <Download className="w-3 h-3" /> Export PDF
              </Button>
            </div>
          </div>

          {selectedEmployee ? (
            <>
              {/* Profile Header */}
              <Card className="bg-white shadow-sm mb-6">
                <CardContent className="p-6">
                  <div className="flex items-start gap-6">
                    <div className={`w-20 h-20 rounded-full flex items-center justify-center text-white text-2xl font-bold ${
                      (selectedEmployee.briScore || 0) >= 85 ? 'bg-[#1B5E20]' : (selectedEmployee.briScore || 0) >= 70 ? 'bg-[#1565C0]' : 'bg-[#F59E0B]'
                    }`}>
                      {selectedEmployee.fullName?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </div>
                    <div className="flex-1">
                      <h2 className="text-xl font-bold text-[#1a3a3a]">{selectedEmployee.fullName}</h2>
                      <p className="text-sm text-muted-foreground">{selectedEmployee.jobTitle}</p>
                      <p className="text-xs text-muted-foreground">Reports to: {selectedEmployee.reportsTo || vpInfo.name}</p>
                      <p className="text-xs text-muted-foreground mt-1">📍 {selectedEmployee.location || 'Kingston'} · 4.7 years · {selectedEmployee.email}</p>
                      <div className="mt-3 p-3 bg-amber-50 border-l-4 border-amber-400 rounded-r">
                        <p className="text-xs text-amber-800">
                          Strong foundation in {selectedEmployee.jobTitle?.includes('Developer') ? 'software development' : 'technical operations'} with demonstrated proficiency in core systems. Primary growth opportunity in cloud-native architecture and automated deployment pipelines.
                        </p>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className={`w-20 h-20 rounded-full border-4 flex items-center justify-center ${
                        (selectedEmployee.briScore || 0) >= 85 ? 'border-[#1B5E20]' : (selectedEmployee.briScore || 0) >= 70 ? 'border-[#1565C0]' : 'border-[#F59E0B]'
                      }`}>
                        <span className={`text-3xl font-bold ${
                          (selectedEmployee.briScore || 0) >= 85 ? 'text-[#1B5E20]' : (selectedEmployee.briScore || 0) >= 70 ? 'text-[#1565C0]' : 'text-[#E65100]'
                        }`}>{selectedEmployee.briScore || '—'}</span>
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-1">/100</p>
                      <GrowthTierBadge tier={selectedEmployee.growthTier} size="lg" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Assessment Breakdown + Skills */}
              <div className="grid grid-cols-2 gap-6 mb-6">
                <Card className="bg-white shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold text-[#1a3a3a]">Assessment Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {selectedEmployee.status === 'completed' ? (
                      <>
                        {[
                          { layer: 'Self-Assessment', score: selectedEmployee.briScore || 0, threshold: 80 },
                          { layer: 'Manager Validation', score: 0, threshold: 80 },
                          { layer: 'Technical Competency', score: 0, threshold: 80 },
                          { layer: 'Hands-On Simulation', score: 0, threshold: 80 },
                          { layer: 'Collaboration & Delivery', score: 0, threshold: 80 },
                        ].map((item) => (
                          <div key={item.layer}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs">{item.layer}</span>
                              <div className="flex items-center gap-2">
                                <span className={`text-sm font-bold ${item.score >= item.threshold ? 'text-emerald-600' : 'text-amber-600'}`}>{item.score}</span>
                                <Badge className={`text-[9px] ${item.score >= item.threshold ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                  {item.score >= item.threshold ? 'ABOVE' : 'BELOW'}
                                </Badge>
                              </div>
                            </div>
                            <Progress value={item.score} className="h-2" />
                          </div>
                        ))}
                        <div className="pt-2 border-t">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-semibold">Weighted Total</span>
                            <span className="text-xl font-bold text-[#1a3a3a]">{selectedEmployee.briScore || 0}</span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-8 text-center">
                        <Clock className="w-12 h-12 text-slate-300 mb-3" />
                        <p className="text-sm font-medium text-slate-500">Awaiting Assessment Data</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {selectedEmployee.status === 'started' ? 'Assessment in progress' : 'Assessment not yet started'}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card className="bg-white shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold text-[#1a3a3a]">Skills Proficiency</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {selectedEmployee.status === 'completed' ? (
                      <>
                        <div className="grid grid-cols-3 gap-2 mb-4">
                          {[
                            { skill: 'Java', rating: 0, proficient: false },
                            { skill: 'AWS', rating: 0, proficient: false },
                            { skill: 'SQL', rating: 0, proficient: false },
                          ].map((s) => (
                            <div key={s.skill} className={`p-2 rounded border border-slate-200 bg-slate-50`}>
                              <p className="text-xs font-medium">{s.skill}</p>
                              <StarRating rating={s.rating} />
                            </div>
                          ))}
                        </div>
                        <div className="pt-4 border-t space-y-2">
                          <p className="text-xs font-semibold">Development Pathway</p>
                          <div className="flex items-center gap-2">
                            <GrowthTierBadge tier={selectedEmployee.growthTier || 'Growing'} />
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-8 text-center">
                        <Clock className="w-12 h-12 text-slate-300 mb-3" />
                        <p className="text-sm font-medium text-slate-500">Awaiting Assessment Data</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Skills data will be available after assessment completion
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Mentorship Pairing Engine */}
              {selectedEmployee.status === 'completed' ? (
                <Card className="bg-white shadow-sm">
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-2">
                      <CardTitle className="text-base font-semibold text-[#1a3a3a]">Recommended Development Partnerships</CardTitle>
                      <Info className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="p-4 bg-slate-50 rounded text-center">
                      <p className="text-sm text-slate-600">
                        Mentorship pairings will be available once more team assessments are completed.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card className="bg-white shadow-sm">
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-2">
                      <CardTitle className="text-base font-semibold text-[#1a3a3a]">Recommended Development Partnerships</CardTitle>
                      <Info className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                      <Clock className="w-12 h-12 text-slate-300 mb-3" />
                      <p className="text-sm font-medium text-slate-500">Awaiting Assessment Data</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Development partnerships will be recommended after assessment completion
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card className="bg-white shadow-sm">
              <CardContent className="p-12 text-center">
                <Users2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">Select a team member from the dropdown above to view their development profile.</p>
              </CardContent>
            </Card>
          )}
        </main>
      )}

      {/* TAB 4: PROJECT READINESS */}
      {activeTab === "readiness" && (
        <main className="max-w-[1500px] mx-auto px-6 py-6">
          <Card className="bg-white shadow-sm mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold text-[#1a3a3a]">Project Readiness Assessment</CardTitle>
              <p className="text-sm text-muted-foreground">Select an initiative type to see how your team's capabilities map to requirements.</p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-6 gap-3">
                {INITIATIVE_TYPES.map((init) => (
                  <button
                    key={init.id}
                    onClick={() => setSelectedInitiative(init)}
                    className={`p-4 rounded-lg border text-center transition-all ${
                      selectedInitiative?.id === init.id 
                        ? 'border-[#1a3a3a] bg-[#E6F0F0] shadow-sm' 
                        : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    <init.icon className={`w-8 h-8 mx-auto mb-2 ${selectedInitiative?.id === init.id ? 'text-[#1a3a3a]' : 'text-slate-500'}`} />
                    <p className="text-xs font-semibold">{init.name}</p>
                    <p className="text-[10px] text-muted-foreground mt-1">Assess →</p>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {selectedInitiative && (
            <>
              {/* Readiness Dashboard */}
              <div className="grid grid-cols-3 gap-6 mb-6">
                {/* Gauge */}
                <Card className="bg-white shadow-sm">
                  <CardContent className="p-6 text-center">
                    <div className="relative w-40 h-40 mx-auto">
                      <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                        <circle cx="50" cy="50" r="40" fill="none" stroke="#e5e5e5" strokeWidth="12" />
                        <circle cx="50" cy="50" r="40" fill="none" stroke="#16A34A" strokeWidth="12" strokeDasharray="188.5 251.3" />
                        <circle cx="50" cy="50" r="40" fill="none" stroke="#1565C0" strokeWidth="12" strokeDasharray="62.8 251.3" strokeDashoffset="-188.5" />
                        <circle cx="50" cy="50" r="40" fill="none" stroke="#F59E0B" strokeWidth="12" strokeDasharray="0 251.3" strokeDashoffset="-251.3" />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <p className="text-3xl font-bold text-emerald-600">75%</p>
                        <p className="text-xs text-muted-foreground">Ready</p>
                      </div>
                    </div>
                    <div className="mt-4 text-xs text-muted-foreground">
                      <span className="text-emerald-600 font-medium">8 ready</span> · 
                      <span className="text-blue-600 font-medium"> 4 trainable</span> · 
                      <span className="text-amber-600 font-medium"> 2 not yet</span>
                    </div>
                  </CardContent>
                </Card>

                {/* Domain Requirements */}
                <Card className="bg-white shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold text-[#1a3a3a]">Domain Requirements</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {[
                      { domain: selectedInitiative.primary, level: 'Primary', need: 70, coverage: 4, total: 6 },
                      { domain: selectedInitiative.secondary, level: 'Secondary', need: 60, coverage: 5, total: 6 },
                      { domain: selectedInitiative.supporting, level: 'Supporting', need: 50, coverage: 6, total: 6 },
                    ].map((req) => (
                      <div key={req.domain} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                        <div>
                          <p className="text-xs font-medium">{req.domain}</p>
                          <p className="text-[10px] text-muted-foreground">{req.level} — need {req.need}+</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs">{req.coverage}/{req.total}</span>
                          {req.coverage >= (req.level === 'Primary' ? 2 : 1) ? (
                            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                          ) : (
                            <AlertCircle className="w-4 h-4 text-amber-500" />
                          )}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Team Readiness List */}
                <Card className="bg-white shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold text-[#1a3a3a]">Team Member Readiness</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {teamMembers.slice(0, 8).map((m, i) => {
                        const status = i < 5 ? 'ready' : i < 7 ? 'trainable' : 'not-yet';
                        return (
                          <div key={m.id} className="flex items-center justify-between py-1 border-b">
                            <span className="text-xs font-medium">{m.fullName}</span>
                            <Badge className={`text-[9px] ${
                              status === 'ready' ? 'bg-emerald-100 text-emerald-700' :
                              status === 'trainable' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'
                            }`}>
                              {status === 'ready' ? '✅ Ready' : status === 'trainable' ? '🔵 Trainable' : '🟡 Not Yet'}
                            </Badge>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Suggested Team */}
              <div className="grid grid-cols-2 gap-6">
                <Card className="bg-white shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold text-[#1a3a3a]">Suggested Core Team</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {teamMembers.filter(m => (m.briScore || 0) >= 70).slice(0, 5).map((m) => (
                        <div key={m.id} className="flex items-center gap-3 p-2 bg-slate-50 rounded">
                          <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center text-white text-xs font-medium">
                            {m.fullName?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                          </div>
                          <div className="flex-1">
                            <p className="text-xs font-medium">{m.fullName}</p>
                            <p className="text-[10px] text-muted-foreground">{m.jobTitle}</p>
                          </div>
                          <Badge className="bg-emerald-100 text-emerald-700 text-[9px]">Ready</Badge>
                        </div>
                      ))}
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-3">This team covers 3/3 required domains with at least 2 proficient members.</p>
                  </CardContent>
                </Card>

                <Card className="bg-white shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold text-[#1a3a3a]">Training to Close Gaps</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {[
                        { domain: selectedInitiative.primary, members: 2, points: 12, months: 3 },
                        { domain: 'AI-Augmented Dev', members: 4, points: 25, months: 6 },
                      ].map((gap, i) => (
                        <div key={i} className="p-3 bg-amber-50 rounded border-l-2 border-amber-400">
                          <p className="text-xs font-medium text-amber-800">{gap.domain}</p>
                          <p className="text-[10px] text-amber-700">{gap.members} team members need ~{gap.points} points of development</p>
                          <p className="text-[10px] text-amber-600">Estimated: {gap.months} months with structured training</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}

          {!selectedInitiative && (
            <Card className="bg-white shadow-sm">
              <CardContent className="p-12 text-center">
                <Target className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">Select an initiative type above to see how your team maps to its requirements.</p>
              </CardContent>
            </Card>
          )}
        </main>
      )}

      {/* TAB 5: VALIDATION QUEUE */}
      {activeTab === "validation" && (
        <main className="max-w-[1500px] mx-auto px-6 py-6">
          <Card className="bg-white shadow-sm mb-6">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-lg font-semibold text-[#1a3a3a]">Awaiting Your Validation</CardTitle>
                  {validations.totalPending > 5 && (
                    <Badge className="bg-amber-100 text-amber-700">Urgent: {validations.totalPending} pending</Badge>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {validations.pending?.length > 0 ? (
                <div className="space-y-3">
                  {validations.pending.map((val) => (
                    <div key={val.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-[#1a3a3a] flex items-center justify-center text-white text-sm font-medium">
                          {val.fullName?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                        </div>
                        <div>
                          <p className="font-medium">{val.fullName}</p>
                          <p className="text-sm text-muted-foreground">{val.jobTitle}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground">Self-Assessment</p>
                          <p className="text-lg font-bold text-blue-600">{val.selfAssessmentScore || 72}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground">Technical</p>
                          <p className="text-lg font-bold text-slate-600">{val.technicalScore || '—'}</p>
                        </div>
                        <Badge className="bg-amber-100 text-amber-700">Awaiting Validation</Badge>
                        <Button className="bg-[#1a3a3a] hover:bg-[#1a3a3a]/90 text-sm">
                          Validate →
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-emerald-500" />
                  <p className="text-muted-foreground">All caught up! No validations pending.</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Completed Validations */}
          <Card className="bg-white shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold text-[#1a3a3a]">Completed Validations ({validations.completed?.length || 0})</CardTitle>
            </CardHeader>
            <CardContent>
              {validations.completed?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-2 text-xs font-medium text-muted-foreground">Employee</th>
                        <th className="text-center py-2 px-2 text-xs font-medium text-muted-foreground">Date</th>
                        <th className="text-center py-2 px-2 text-xs font-medium text-muted-foreground">Endorsement</th>
                        <th className="text-center py-2 px-2 text-xs font-medium text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {validations.completed.map((val) => (
                        <tr key={val.id} className="border-b">
                          <td className="py-2 px-2 font-medium">{val.employeeName}</td>
                          <td className="py-2 px-2 text-center text-muted-foreground">{val.completedDate || 'Jan 28'}</td>
                          <td className="py-2 px-2 text-center">
                            <Badge className="bg-emerald-100 text-emerald-700">Strong Contributor</Badge>
                          </td>
                          <td className="py-2 px-2 text-center">
                            <Button variant="ghost" size="sm" className="text-xs">View</Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-center py-8 text-muted-foreground">No completed validations yet.</p>
              )}
            </CardContent>
          </Card>
        </main>
      )}

      {/* Footer */}
      <footer className="border-t bg-white py-4 mt-8">
        <div className="max-w-[1500px] mx-auto px-6">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <div className="flex items-center gap-2">
              <RefreshCw className="w-3 h-3" />
              Last updated: just now
            </div>
            <p>THCO Platform | 5-Layer Methodology™ | Team View</p>
            <div className="flex items-center gap-4">
              <span className="cursor-pointer hover:text-foreground">Help</span>
              <span className="cursor-pointer hover:text-foreground">Export</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
